import { Dialog } from "primereact/dialog";
import { useDispatch, useSelector } from "react-redux";
import { IATECaseData } from "src/redux/interfaces/IATECase";
import {
  clearATECasePopupData,
  setIsATECasesDetailsPopupOpen
} from "src/redux/slices/ateCasesSlice";
import { useTranslation } from "react-i18next";
import {
  formatDateV2,
  getClassesBasedOnStatus,
  getFieldFieldValue,
  getStatusByStatusId,
  getTextValueById,
  isCivilDefenseUser
} from "src/utils/helpers";
import SLABreached from "src/components/SLABreached/SLABreached";
import VerticalDivider from "src/components/VerticalDivider/VerticalDivider";
import { useState } from "react";
import { StatusesWithHoldReason } from "src/resources/enum";

const ATECasesDetailsPopup = () => {
  const dispatch = useDispatch();

  const { t } = useTranslation(["common", "registration"]);

  const { caseId } = useSelector(
    (state: any) => state.ateCases.ateCasesDetailsPopupData
  );
  function selectATECase(): (state: any) => any {
    return (state: any) =>
      state.ateCases.ateCasesData?.find(
        (ateCase: IATECaseData) => ateCase.ticketNumber === caseId
      );
  }
  const selectedATECase = useSelector(selectATECase());
  const pathName = window.location.pathname;
  const [isBreached, setIsBreached] = useState<any>(
    pathName?.includes("breached") ? true : false
  );
  const isATECasesDetailsPopupOpen = useSelector(
    (state: any) => state.ateCases.isATECasesDetailsPopupOpen
  );
  const {
    statusOptions,
    onHoldReasons,
    systems,
    citiesOptions,
    emiratesOptions,
    allServiceTerritories,
  } = useSelector((state: any) => state.lookup);

  const getOnHoldReason = () => {
    const onHoldReason = onHoldReasons?.find(
      (x: { id: number }) => x.id == selectedATECase?.onHoldReasonId
    );
    return getFieldFieldValue(onHoldReason);
  };

  function closePopup(): void {
    dispatch(setIsATECasesDetailsPopupOpen(false));
    dispatch(clearATECasePopupData());
  }

  const StatusTemplate = () => {
    const status = getStatusByStatusId(
      statusOptions,
      selectedATECase?.statusCodeId
    );
    return (
      <span className={`oval ${getClassesBasedOnStatus(status)}`}>
        {getFieldFieldValue(status)}
      </span>
    );
  };
  const systemType = getTextValueById(
    selectedATECase?.systemServiceId,
    systems as unknown as [],
    "systemServiceId"
  );

  function getEmirate() {
    return getTextValueById(
      selectedATECase?.emirateId,
      emiratesOptions,
      "emirateId"
    );
  }

  function getCity() {
    return getTextValueById(selectedATECase?.cityId, citiesOptions, "cityId");
  }

  function getTerritory() {
    return getTextValueById(
      selectedATECase?.serviceTerritoryId,
      allServiceTerritories as unknown as [],
      "territoryId"
    );
  }

  function checkIfPendingOwnerApproval() {
    const status = getStatusByStatusId(
      statusOptions,
      selectedATECase?.statusCodeId
    );
    return status?.englishName?.toLowerCase() === StatusesWithHoldReason.PendingOwnerApproval;
  }

  return (
    <Dialog
      visible={isATECasesDetailsPopupOpen}
      onHide={closePopup}
      header={t("faultDetails")}
      headerClassName="popup-header"
      className="maintenance-requests-details-popup"
    >
      <div className="pt-3">
        <div className="d-flex justify-content-between flex-wrap  gap-2 align-items-center mb-4 popup-top-section">
          <SLABreached sLABreached={selectedATECase?.sLABreached} />

        </div>
        <section>
          <h2 className="font-weight-700">{t("status")}</h2>
          <div className="section-body">
            <div className={`section-body__item  w-32 w-md-100 ${checkIfPendingOwnerApproval()? "py-0 my-auto" : ""}`}>
              <StatusTemplate />
            </div>

            {checkIfPendingOwnerApproval() && (
              <>
                <VerticalDivider />
                <div className="section-body__item w-32 w-md-100">
                  <h4 className="font-weight-700">{t("typeOfApproval")}</h4>
                  <span>
                    <span>{getOnHoldReason()}</span>
                  </span>
                </div>{" "}
              </>
            )}
          </div>
        </section>

        <section>
          <h2 className="font-weight-700">{t("faultDetails")}</h2>
          <div className="section-body">
            <div className="section-body__item small__item">
              <h4 className="font-weight-700">{t("caseId")}</h4>
              <span>{caseId}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item  w-32 w-md-100">
              <h4 className="font-weight-700">{t("systemName")}</h4>
              <span>{systemType}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item  w-32 w-md-100">
              <h4 className="font-weight-700">{t("errorCodeDescription")}</h4>
              <span>{selectedATECase?.deviceErrorCodeDescription}</span>
            </div>
          </div>
          <div className="section-body">
            <div className="section-body__item small__item">
              <h4 className="font-weight-700">{t("creationDate")}</h4>
              <span className="plain-text-date">
                {formatDateV2(selectedATECase?.createdOn)}
              </span>
            </div>
            <VerticalDivider />
            <div className="section-body__item w-32 w-md-100">
              {!isBreached && (
                <>
                  {" "}
                  <h4 className="font-weight-700">{t("resolutionDate")}</h4>
                  <span className="plain-text-date">
                    {formatDateV2(selectedATECase?.resolutionDate)}
                  </span>
                </>
              )}
              {isBreached && (
                <>
                  {" "}
                  <h4 className="font-weight-700">{t("systemSubtype")}</h4>
                  <span>{selectedATECase?.systemSubtype}</span>
                </>
              )}
            </div>
            <VerticalDivider />
            <div className="section-body__item w-32 w-md-100">
              <h4 className="font-weight-700">{t("managerSupervisorName")}</h4>
              <span>{selectedATECase?.managerSupervisorName}</span>
            </div>
          </div>

          {isCivilDefenseUser() && (
            <div className="section-body">
              <div className="section-body__item  w-32 w-md-100">
                <h4 className="font-weight-700">{t("faultLocation")}</h4>
                <span>{selectedATECase?.faultLocation}</span>
              </div>
              <VerticalDivider />
              <div className="section-body__item w-32 w-md-100">
                <h4 className="font-weight-700">{t("theFaultDescription")}</h4>
                <span>{selectedATECase?.faultDescription}</span>
              </div>
              {!isBreached && (
                <>
                  <VerticalDivider />
                  <div className="section-body__item">
                    <h4 className="font-weight-700">{t("systemSubtype")}</h4>
                    <span>{selectedATECase?.systemSubtype}</span>
                  </div>
                </>
              )}
            </div>
          )}
        </section>

        <section>
          <h2 className="font-weight-700">
            {isCivilDefenseUser()
              ? t("bmcAndPropertyDetails")
              : t("propertyDetails")}
          </h2>
          <div className="section-body">
            <div className="section-body__item small__item">
              <h4 className="font-weight-700">{t("propertyID")}</h4>
              <span>{selectedATECase?.buildingNumber}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item small__item">
              <h4 className="font-weight-700">{t("buildingNameHeader")}</h4>
              <span>{selectedATECase?.buildingName}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item  w-32 w-md-100">
              <h4 className="font-weight-700">{t("emirate")}</h4>
              <span>{getEmirate()}</span>
            </div>
          </div>
          <div className="section-body">
            <div className="section-body__item small__item">
              <h4 className="font-weight-700">{t("city")}</h4>
              <span>{getCity()}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item small__item">
              <h4 className="font-weight-700">{t("territory")}</h4>
              <span>{getTerritory()}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item  w-32 w-md-100">
              <h4 className="font-weight-700">{t("latitude")}</h4>
              <span>{selectedATECase?.latitude}</span>
            </div>
          </div>
          <div className="section-body">
            <div className="section-body__item small__item">
              <h4 className="font-weight-700">{t("longitude")}</h4>
              <span>{selectedATECase?.longitude}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item  w-32 w-md-100">
              <h4 className="font-weight-700">{t("address")}</h4>
              <span>{selectedATECase?.buildingAddress}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item small__item">
              <h4 className="font-weight-700">
                {t("contractReferenceNumber")}
              </h4>
              <span>{selectedATECase?.contractNumber}</span>
            </div>
          </div>
          {isCivilDefenseUser() && (
            <div className="section-body">
              <div className="section-body__item w-32 w-md-100">
                <h4 className="font-weight-700">{t("bmcCompanyName")}</h4>
                <span>{selectedATECase?.companyName}</span>
              </div>
            </div>
          )}
        </section>

        <section>
          <h2 className="font-weight-700">{t("contactDetails")}</h2>
          <div className="section-body">
            <div className="section-body__item  w-32 w-md-100">
              <h4 className="font-weight-700">{t("technicianName")}</h4>
              <span>{selectedATECase?.engineerTechnicianName}</span>
            </div>
            {isCivilDefenseUser() && (
              <>
                <VerticalDivider />
                <div className="section-body__item w-32 w-md-100">
                  <h4 className="font-weight-700">{t("bldgContactPerson")}</h4>
                  <span>{selectedATECase?.buildingContactPerson}</span>
                </div>
                <VerticalDivider />
                <div className="section-body__item">
                  <h4 className="font-weight-700">{t("phone")}</h4>
                  <span>{selectedATECase?.contactPersonMobileNumber}</span>
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </Dialog>
  );
};

export default ATECasesDetailsPopup;
